import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays, List as ListIcon, Plus, Trash2, Pencil, Users, X, ChevronLeft, ChevronRight, Coffee, ClipboardList, Handshake, StickyNote } from "lucide-react";

// Configurazione tipi eventi
const EVENT_TYPES = [
  { value: "assenza",  label: "Assenza",  icon: Coffee,          color: "bg-amber-100  border-amber-400  text-amber-900" },
  { value: "task",     label: "Task",     icon: ClipboardList,   color: "bg-blue-100   border-blue-400   text-blue-900" },
  { value: "riunione", label: "Riunione", icon: Handshake,       color: "bg-purple-100 border-purple-400 text-purple-900" },
  { value: "nota",     label: "Nota",     icon: StickyNote,      color: "bg-slate-100  border-slate-400  text-slate-800" },
];
const typeCfg = (t) => EVENT_TYPES.find((x) => x.value === t) || EVENT_TYPES[3];

const pad2 = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayIso = () => isoDay(new Date());
const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function AgendaPage() {
  const { user } = useAuth();
  const canEdit = ["admin", "magazziniere", "assistente"].includes(user?.role);
  const [view, setView] = useState("calendar"); // calendar | list
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [busy, setBusy] = useState(false);

  // Form / dialog
  const emptyForm = { type: "task", title: "", description: "", date_from: todayIso(), date_to: "", assigned_to: [] };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  // Range del mese visualizzato (per fetch)
  const range = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    return {
      from: isoDay(new Date(y, m, 1)),
      to:   isoDay(new Date(y, m + 1, 0)),
    };
  }, [cursor]);

  const loadEvents = async () => {
    setBusy(true);
    try {
      const params = { date_from: range.from, date_to: range.to };
      if (filterType !== "all") params.type = filterType;
      if (filterUser !== "all") params.assigned_to = filterUser;
      const { data } = await api.get("/agenda-events", { params });
      setEvents(data || []);
    } catch { toast.error("Errore caricamento agenda"); }
    finally { setBusy(false); }
  };
  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, [range.from, range.to, filterType, filterUser]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const openNew = (preFillDate) => {
    if (!canEdit) return;
    setForm({ ...emptyForm, date_from: preFillDate || todayIso() });
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (ev) => {
    if (!canEdit) return;
    setEditingId(ev.id);
    setForm({
      type: ev.type,
      title: ev.title,
      description: ev.description || "",
      date_from: ev.date_from,
      date_to: ev.date_to || "",
      assigned_to: ev.assigned_to || [],
    });
    setOpen(true);
  };

  const saveEvent = async () => {
    if (!form.title.trim()) return toast.error("Titolo obbligatorio");
    if (!form.date_from) return toast.error("Data inizio obbligatoria");
    if (form.date_to && form.date_to < form.date_from) return toast.error("Data fine precede data inizio");
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        title: form.title.trim(),
        description: form.description || "",
        date_from: form.date_from,
        date_to: form.date_to || null,
        assigned_to: form.assigned_to || [],
      };
      if (editingId) {
        await api.patch(`/agenda-events/${editingId}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await api.post("/agenda-events", payload);
        toast.success("Evento creato");
      }
      setOpen(false);
      await loadEvents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Eliminare "${ev.title}"?`)) return;
    try {
      await api.delete(`/agenda-events/${ev.id}`);
      toast.success("Rimosso");
      await loadEvents();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Grid mensile
  const monthGrid = useMemo(() => {
    const y = cursor.getFullYear(); const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    // Lunedì = 0
    const startWeekday = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const from = ev.date_from;
      const to = ev.date_to || ev.date_from;
      const d0 = new Date(from);
      const d1 = new Date(to);
      for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
        const k = isoDay(d);
        if (!map[k]) map[k] = [];
        map[k].push(ev);
      }
    }
    return map;
  }, [events]);

  const toggleAssignee = (uid) => {
    setForm((f) => ({ ...f, assigned_to: f.assigned_to.includes(uid) ? f.assigned_to.filter((x) => x !== uid) : [...f.assigned_to, uid] }));
  };
  const assigneesLabel = form.assigned_to.length === 0
    ? "Nessuno (evento generale)"
    : form.assigned_to.length === 1
      ? users.find((u) => u.id === form.assigned_to[0])?.name || "1 utente"
      : `${form.assigned_to.length} utenti`;

  const changeMonth = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Agenda</h1>
          <p className="text-sm text-slate-500">Assenze, task, riunioni e note condivise del team</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-sm border border-slate-200 bg-white p-0.5">
            <button
              onClick={() => setView("calendar")}
              className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium ${view === "calendar" ? "bg-[#0052FF] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="agenda-view-calendar"
            >
              <CalendarDays className="h-4 w-4" /> Calendario
            </button>
            <button
              onClick={() => setView("list")}
              className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium ${view === "list" ? "bg-[#0052FF] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="agenda-view-list"
            >
              <ListIcon className="h-4 w-4" /> Lista
            </button>
          </div>
          {canEdit && (
            <Button onClick={() => openNew()} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="agenda-new">
              <Plus className="mr-1.5 h-4 w-4" /> Nuovo evento
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-sm border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" onClick={() => changeMonth(-1)} data-testid="agenda-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-40 text-center text-sm font-bold text-slate-900" data-testid="agenda-cursor-label">
            {MONTHS_IT[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" onClick={() => changeMonth(1)} data-testid="agenda-next-month"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="ml-2 h-8 rounded-sm" onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} data-testid="agenda-today">Oggi</Button>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-8 w-40 rounded-sm text-sm" data-testid="agenda-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="h-8 w-56 rounded-sm text-sm" data-testid="agenda-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli utenti</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-xs text-slate-400">({u.role})</span></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="rounded-sm border border-slate-200 bg-white p-2">
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS_IT.map((w) => (
              <div key={w} className="p-2 text-center text-[10px] font-bold uppercase tracking-widest text-slate-500">{w}</div>
            ))}
            {monthGrid.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} className="h-24 rounded-sm bg-slate-50" />;
              const key = isoDay(day);
              const isToday = key === todayIso();
              const dayEvents = eventsByDay[key] || [];
              return (
                <div
                  key={key}
                  className={`h-24 rounded-sm border p-1 text-xs ${isToday ? "border-[#0052FF] bg-blue-50/40" : "border-slate-200 bg-white hover:bg-slate-50"} cursor-pointer overflow-hidden`}
                  onClick={() => openNew(key)}
                  data-testid={`agenda-day-${key}`}
                >
                  <div className={`mb-1 flex items-center justify-between ${isToday ? "text-[#0052FF] font-bold" : "text-slate-700"}`}>
                    <span className="font-mono">{day.getDate()}</span>
                    {dayEvents.length > 0 && <span className="rounded-sm bg-slate-800 px-1 text-[9px] font-bold text-white">{dayEvents.length}</span>}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 2).map((ev) => {
                      const cfg = typeCfg(ev.type);
                      return (
                        <div
                          key={ev.id}
                          onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                          className={`truncate rounded-sm border px-1 py-0.5 text-[10px] font-medium ${cfg.color}`}
                          title={`${ev.title}${ev.assigned_to_names?.length ? " — " + ev.assigned_to_names.join(", ") : ""}`}
                        >
                          <cfg.icon className="mr-0.5 inline h-2.5 w-2.5" />
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 2 && (
                      <div className="text-[9px] text-slate-500">+{dayEvents.length - 2} altri</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-sm border border-slate-200 bg-white">
          {events.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Nessun evento in questo mese</div>}
          {events.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">
                <tr>
                  <th className="p-2 text-left">Tipo</th>
                  <th className="p-2 text-left">Titolo</th>
                  <th className="p-2 text-left">Dal</th>
                  <th className="p-2 text-left">Al</th>
                  <th className="p-2 text-left">Assegnato a</th>
                  <th className="p-2 text-left">Creato da</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const cfg = typeCfg(ev.type);
                  return (
                    <tr key={ev.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`agenda-row-${ev.id}`}>
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase ${cfg.color}`}>
                          <cfg.icon className="h-3 w-3" /> {cfg.label}
                        </span>
                      </td>
                      <td className="p-2 font-medium text-slate-900">{ev.title}{ev.description ? <div className="text-[11px] text-slate-500">{ev.description}</div> : null}</td>
                      <td className="p-2 font-mono text-slate-700">{ev.date_from}</td>
                      <td className="p-2 font-mono text-slate-700">{ev.date_to || "—"}</td>
                      <td className="p-2 text-slate-700">{ev.assigned_to_names?.length ? ev.assigned_to_names.join(", ") : <span className="italic text-slate-400">generale</span>}</td>
                      <td className="p-2 text-slate-500">{ev.created_by_name || "—"}</td>
                      <td className="p-2 text-right">
                        {canEdit && (
                          <>
                            <button type="button" onClick={() => openEdit(ev)} className="mr-1 rounded-sm border border-slate-300 bg-white px-1.5 py-0.5 text-slate-700 hover:bg-slate-100" data-testid={`agenda-edit-${ev.id}`}><Pencil className="h-3 w-3" /></button>
                            <button type="button" onClick={() => deleteEvent(ev)} className="rounded-sm border border-red-300 bg-white px-1.5 py-0.5 text-red-700 hover:bg-red-50" data-testid={`agenda-delete-${ev.id}`}><Trash2 className="h-3 w-3" /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Dialog form */}
      <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) setOpen(false); }}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-[#0052FF]" />
              {editingId ? "Modifica evento" : "Nuovo evento"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">Tipo *</Label>
              <div className="mt-1 grid grid-cols-4 gap-1.5">
                {EVENT_TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ ...form, type: t.value })}
                    className={`flex flex-col items-center gap-1 rounded-sm border p-2 text-xs font-medium ${form.type === t.value ? `${t.color} border-2` : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}
                    data-testid={`agenda-type-${t.value}`}
                  >
                    <t.icon className="h-4 w-4" />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Ferie Marco / Task consegna materiali" className="rounded-sm" data-testid="agenda-title" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Dal *</Label>
                <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} className="rounded-sm font-mono" data-testid="agenda-from" />
              </div>
              <div>
                <Label className="text-xs">Al (opzionale)</Label>
                <Input type="date" value={form.date_to || ""} onChange={(e) => setForm({ ...form, date_to: e.target.value })} placeholder="stessa data" className="rounded-sm font-mono" data-testid="agenda-to" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Assegnato a</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 w-full justify-between rounded-sm bg-white font-normal" data-testid="agenda-assignees-trigger">
                    <span className="truncate">
                      <Users className="mr-1.5 inline h-3.5 w-3.5" />
                      {assigneesLabel}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 rounded-sm p-2" align="start">
                  <div className="max-h-64 space-y-1 overflow-auto">
                    {users.map((u) => (
                      <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 hover:bg-slate-100" data-testid={`agenda-user-opt-${u.id}`}>
                        <Checkbox checked={form.assigned_to.includes(u.id)} onCheckedChange={() => toggleAssignee(u.id)} />
                        <span className="text-sm">{u.name}</span>
                        <span className="ml-auto text-[10px] uppercase text-slate-400">{u.role}</span>
                      </label>
                    ))}
                  </div>
                  {form.assigned_to.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, assigned_to: [] })} className="mt-1 h-7 w-full rounded-sm text-xs">
                      Reset selezione
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Dettagli evento…" className="rounded-sm" data-testid="agenda-description" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editingId && canEdit && (
              <Button variant="outline" onClick={() => { deleteEvent({ id: editingId, title: form.title }); setOpen(false); }} className="mr-auto rounded-sm border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="mr-1 h-4 w-4" /> Elimina
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-sm">Annulla</Button>
            <Button onClick={saveEvent} disabled={busy || !form.title.trim()} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="agenda-save">
              {busy ? "Salvataggio…" : (editingId ? "Aggiorna" : "Crea")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
